/**
 * Safe loader for React Router `lazy` options.
 *
 * Two deploy-time failure modes show up as runtime crashes otherwise:
 *   1. `import()` rejects — network error or the chunk was deleted. Already
 *      caught by `chunk-error-handler.ts` via `unhandledrejection`, but the
 *      route's lazy() resolver sees the error first and surfaces it inside
 *      React Router's error boundary instead.
 *   2. `import()` resolves, but the returned module is missing the expected
 *      named export — e.g. when the CDN 200s with `index.html` instead of
 *      the missing JS. Destructuring then crashes with
 *      `Cannot destructure property 'X' of '(intermediate value)' as it is
 *      undefined`, which is NOT detected as a chunk error and falls through
 *      to Sentry.
 *
 * This helper validates the shape, retries once on failure, and — if the
 * retry also fails — throws an error whose message matches the existing
 * `isChunkLoadError` patterns so the global handler clears caches and
 * reloads instead of white-screening the user.
 */

import type { ComponentType } from 'react';
import { handleChunkError } from './chunk-error-handler';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyComponent = ComponentType<any>;

const RETRY_DELAY_MS = 300;

const delay = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

export const lazyRoute = async <Name extends string>(
  importFn: () => Promise<Record<string, unknown>>,
  exportName: Name,
): Promise<{ Component: AnyComponent }> => {
  const load = async (): Promise<AnyComponent> => {
    const mod = await importFn();
    const Component = mod?.[exportName] as AnyComponent | undefined;
    if (!Component) {
      // Matches `chunk-error-handler`'s `ChunkLoadError` pattern so the
      // global recovery path kicks in.
      throw new Error(
        `ChunkLoadError: module loaded without expected export "${exportName}" (stale chunk)`,
      );
    }
    return Component;
  };

  try {
    return { Component: await load() };
  } catch (firstError) {
    try {
      await delay(RETRY_DELAY_MS);
      return { Component: await load() };
    } catch (secondError) {
      // Fire-and-forget recovery; also re-throw so React Router surfaces the
      // error to the boundary if the reload somehow doesn't happen.
      void handleChunkError(secondError);
      throw secondError;
    }
  }
};

export const lazyDefault = async (
  importFn: () => Promise<{ default: AnyComponent }>,
): Promise<{ Component: AnyComponent }> => lazyRoute(importFn as () => Promise<Record<string, unknown>>, 'default');
